import { useCallback, useEffect, useMemo, useState } from "react";
import { useOneOrgContext } from "@/auth/OneOrgContext";
import { apiGet } from "@/lib/apiClient";
import { articleBelongsToKnowledgeBase } from "@/lib/knowledgeBaseRegistry";
import type { KnowledgeArticle } from "@/lib/knowledgeModel";
import { useKnowledgeBases } from "@/pages/knowledge/useKnowledgeBases";

type ArticlesResponse = {
  articles?: KnowledgeArticle[];
  error?: string;
};

/**
 * Loads org articles from GET /api/knowledge/articles, then scopes them to the
 * active knowledge base (kb:<id> membership). Pass scoped: false for discovery.
 */
export function useKnowledgeArticles(limit = 500, options?: { scoped?: boolean }) {
  const scoped = options?.scoped !== false;
  const { orgContextStatus, orgContextError } = useOneOrgContext();
  const { activeId, mergeFromArticles } = useKnowledgeBases();
  const [allArticles, setAllArticles] = useState<KnowledgeArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (orgContextStatus === "loading") {
      setLoading(true);
      setError(null);
      return;
    }
    if (orgContextStatus === "missing") {
      setAllArticles([]);
      setError(orgContextError || "Organization context required.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    const res = await apiGet<ArticlesResponse>(`/api/knowledge/articles?limit=${limit}`);
    if (!res.ok) {
      setError(res.error || "Could not load articles.");
      setAllArticles([]);
    } else {
      const rows = Array.isArray(res.data?.articles) ? res.data.articles : [];
      setAllArticles(rows);
      mergeFromArticles(rows);
    }
    setLoading(false);
  }, [limit, orgContextStatus, orgContextError, mergeFromArticles]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const articles = useMemo(() => {
    if (!scoped) return allArticles;
    return allArticles.filter((article) => articleBelongsToKnowledgeBase(article, activeId));
  }, [allArticles, scoped, activeId]);

  const setArticles = useCallback(
    (updater: KnowledgeArticle[] | ((prev: KnowledgeArticle[]) => KnowledgeArticle[])) => {
      setAllArticles((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        return next;
      });
    },
    [],
  );

  return { articles, allArticles, setArticles, loading, error, reload, activeKbId: activeId };
}
